import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  WORKFLOW_ACTIONS,
} from '../../../components/Admin/Workflows/Wizard/WorkflowContext';
import {
  NODE_CATEGORIES,
  NODE_TYPES,
  WORKFLOW_DEFAULTS,
  APPROVAL_TYPE_CODES,
} from '../../../constants/workflow.constant';
import Typography from '../../../components/common/StyledTypography';

const useDragAndDrop = ({ reactFlowInstance, approvalTypes }) => {
  const { t } = useTranslation();
  const { nodes, lastNodeId, setNodes, setEdges, dispatch } = useWorkflow();

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      const dataStr = event.dataTransfer.getData('data');
      const data = dataStr ? JSON.parse(dataStr) : {};

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeId = `${type}-${nodes.length}-${Math.floor(Math.random() * 10000)}`;

      let newNode = {};

      if (
        type.startsWith('approval-') &&
        data.code === APPROVAL_TYPE_CODES.SEND_TO_ASSESSMENT
      ) {
        const approvalTypeId = approvalTypes.find(
          (approvalType) =>
            approvalType.code === APPROVAL_TYPE_CODES.SEND_TO_ASSESSMENT
        )?.id;
        newNode = {
          id: nodeId,
          type: type,
          scope: 'ReviewChildNode',
          category: NODE_CATEGORIES.CHILD_STA,
          data: {
            label: (
              <Typography component="h6" variant="subtitle">
                {data.name}
              </Typography>
            ),
            approvalTypeId,
            name: data.name,
            formConfig: {},
          },
          className: 'child-node',
          position,
        };

        const reviewNodeId = `FormStage-assessment-review-${nodeId}-${Math.floor(Math.random() * 10000)}`;
        const autoReviewNode = {
          id: reviewNodeId,
          type: NODE_TYPES.FORM_STAGE,
          position: { x: position.x, y: position.y + 120 },
          category: NODE_CATEGORIES.ASSESSMENT_REVIEW,
          data: {
            label: (
              <Typography component="h6" variant="subtitle">
                {t('workflowBuilder.assessmentReviewStage')}
              </Typography>
            ),
            name: t('workflowBuilder.assessmentReviewStage'),
            id: null,
            formConfig: null,
          },
        };

        setNodes((nds) => [...nds, newNode, autoReviewNode]);

        const newEdge = {
          id: `edge-${nodeId}`,
          source: nodeId,
          target: reviewNodeId,
          type: WORKFLOW_DEFAULTS.EDGE_TYPE,
          style: WORKFLOW_DEFAULTS.EDGE_STYLE,
        };
        setEdges((eds) => [...eds, newEdge]);

        setTimeout(() => {
          dispatch({
            type: WORKFLOW_ACTIONS.SET_OPEN_STAGE_FORM_MODAL,
            payload: {
              open: true,
              nodeStageId: reviewNodeId,
              content: autoReviewNode.data.formConfig,
            },
          });
        }, 100);
        return;
      }

      if (type.startsWith('approval-')) {
        const categoryMap = {
          app: NODE_CATEGORIES.CHILD_APP,
          rej: NODE_CATEGORIES.CHILD_REJ,
          sbk: NODE_CATEGORIES.CHILD_SBK,
        };

        newNode = {
          id: nodeId,
          type: type,
          scope: 'ReviewChildNode',
          category: categoryMap[data.code] || NODE_CATEGORIES.CHILD_APP,
          data: {
            label: (
              <Typography component="h6" variant="subtitle">
                {data.name}
              </Typography>
            ),
            approvalTypeId: data.approvalTypeId,
            name: data.name,
            formConfig: {},
          },
          className: 'child-node',
          position,
        };

        setNodes((nds) => [...nds, newNode]);
        return;
      }

      if (type === NODE_TYPES.FORM_STAGE) {
        newNode = {
          id: nodeId,
          type: NODE_TYPES.FORM_STAGE,
          position,
          category: NODE_CATEGORIES.STAGE,
          data: {
            label: (
              <Typography component="h6" variant="subtitle">
                {t('workflowBuilder.stage')}
              </Typography>
            ),
            name: t('workflowBuilder.stage'),
            id: null,
            formConfig: null,
          },
        };
        dispatch({
          type: WORKFLOW_ACTIONS.SET_OPEN_STAGE_FORM_MODAL,
          payload: { open: true, nodeStageId: nodeId, content: null },
        });
      } else {
        newNode = {
          id: `output-${nodeId}`,
          type: NODE_TYPES.OUTPUT,
          position,
          category: NODE_CATEGORIES.END,
          data: {
            label: (
              <Typography component="h6" variant="subtitle">
                {t('workflowBuilder.endFlow')}
              </Typography>
            ),
            name: t('workflowBuilder.endFlow'),
            isEndNode: true,
          },
        };
      }

      setNodes((nds) => [...nds, newNode]);

      if (lastNodeId !== nodeId && nodes.length === 1) {
        const newEdge = {
          id: `edge-${newNode.id}`,
          source: WORKFLOW_DEFAULTS.START_NODE_ID,
          target: newNode.id,
          type: WORKFLOW_DEFAULTS.EDGE_TYPE,
          style: WORKFLOW_DEFAULTS.EDGE_STYLE,
        };
        setEdges((eds) => [...eds, newEdge]);
      }

      dispatch({
        type: WORKFLOW_ACTIONS.SET_LAST_NODE_ID,
        payload: newNode.id,
      });
    },
    [
      lastNodeId,
      reactFlowInstance,
      setEdges,
      setNodes,
      nodes,
      t,
      approvalTypes,
      dispatch,
    ]
  );

  return { onDrop, onDragOver };
};

export default useDragAndDrop;
